<template>
  <BaseDialog
    :value="isOpen"
    title="Cancel ongoing uploads?"
    subtitle="Some files are still uploading"
    :show-footer="true"
    :show-cancel="true"
    cancel-label="Keep uploading"
    :show-confirm="true"
    confirm-label="Cancel uploads"
    confirm-button-class="base-dialog__negative-btn"
    :persistent="true"
    width="480px"
    @input="$emit('update:isOpen', $event)"
    @confirm="handleCancelUploads"
    @cancel="handleKeepUploads"
    @hide="$emit('hide')"
  >
    <div class="upload-cancellation-content">
      <div class="upload-info">
        <q-icon name="upload" size="24px" color="warning" class="upload-icon" />
        <div class="upload-text">
          <p class="upload-message">
            <strong>{{ activeUploadsCount }}</strong> file{{ activeUploadsCount > 1 ? 's are' : ' is' }} still uploading.
          </p>
          <p class="upload-description">Cancelling will stop all uploads and you'll need to restart them.</p>
        </div>
      </div>

      <div v-if="uploadsToShow.length > 0" class="upload-list">
        <div v-for="upload in uploadsToShow" :key="upload.id" class="upload-item">
          <div class="upload-item-info">
            <span class="file-name">{{ truncateFileName(upload.fileName) }}</span>
            <span class="upload-progress">{{ Math.round(upload.progress) }}%</span>
          </div>
          <q-linear-progress :value="upload.progress / 100" color="primary" size="4px" class="upload-progress-bar" />
        </div>
      </div>
    </div>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@components/base/BaseDialog.vue';

export default {
  name: 'UploadCancellationDialog',
  components: {
    BaseDialog
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    uploads: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    activeUploads() {
      return this.uploads.filter(upload => !(upload.progress >= 100 || upload.status === 'error'));
    },

    activeUploadsCount() {
      return this.activeUploads.length;
    },

    // Show max 3 uploads in the list
    uploadsToShow() {
      return this.activeUploads.slice(0, 3);
    }
  },
  methods: {
    handleCancelUploads() {
      this.$emit('cancel-uploads');
    },

    handleKeepUploads() {
      this.$emit('keep-uploads');
    },

    truncateFileName(fileName) {
      if (!fileName) return '';
      if (fileName.length <= 30) return fileName;

      const extension = fileName.split('.').pop();
      const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
      const truncatedName = nameWithoutExt.substring(0, 20);

      return `${truncatedName}...${extension}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.upload-cancellation-content {
  .upload-info {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 20px;

    .upload-icon {
      flex-shrink: 0;
      margin-top: 2px;
    }

    .upload-text {
      flex: 1;

      .upload-message {
        @include inter-font($size: 15px, $bolded: false, $color: var(--main-text-color), $weight: 500);
        margin: 0 0 8px 0;
        line-height: 1.4;
      }

      .upload-description {
        @include inter-font($size: 14px, $bolded: false, $color: var(--secondary-text-color), $weight: 400);
        margin: 0;
        line-height: 1.4;
      }
    }
  }

  .upload-list {
    background: var(--secondary-bg-color);
    border-radius: 8px;
    padding: 16px;
    border: 1px solid var(--grey-hr);

    .upload-item {
      &:not(:last-child) {
        margin-bottom: 16px;
      }

      .upload-item-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;

        .file-name {
          @include inter-font($size: 13px, $bolded: false, $color: var(--main-text-color), $weight: 500);
          flex: 1;
        }

        .upload-progress {
          @include inter-font($size: 13px, $bolded: true, $color: var(--secondary-text-color), $weight: 600);
          margin-left: 12px;
        }
      }

      .upload-progress-bar {
        border-radius: 2px;
      }
    }
  }
}
</style>
